import { BellOutlined } from '@ant-design/icons'
import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined'
import { useAuth } from '@services/auth'
import { useChargeEventNotification } from '@services/notification'
import { usePageHeader } from '@shared/innmaxLib/hooks/usePageHeader'
import useViewport from '@shared/innmaxLib/hooks/useViewport'
import useVisible from '@shared/innmaxLib/hooks/useVisible'
import { Modal, Table } from '@shared/innmaxUI'
import { Dropdown } from '@shared/innmaxUI/Dropdown'
import { ModalProps } from '@shared/innmaxUI/Modal'
import { Button, Layout } from 'antd'
import { pathOr } from 'ramda'
import React from 'react'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ChangePasswordModal from './ChangePasswordModal'
import MobileNavbar from './MobileNavbar'

type Props = {
  title?: string | React.ReactNode
}

function Navbar({ title }: Props) {
  const { logout, user } = useAuth()

  const { isMobile } = useViewport()

  const handleLogout = () => logout()

  const {
    visible: visibleNotificationModal,
    toggle: toggleVisibleNotificationModal,
  } = useVisible()

  const [openChangePasswordModal, setOpenChangePasswordModal] =
    React.useState(false)

  const handleChangePassword = () => {
    setOpenChangePasswordModal(true)
  }

  const { t } = useTranslation()

  const extraContent = usePageHeader().content

  React.useEffect(() => {
    const titleElement = document.getElementById('header-title') as HTMLElement
    titleElement &&
      (titleElement.innerHTML = renderToString(
        title as React.ReactElement<React.ReactNode>
      ))
  }, [JSON.stringify((title as any)?.props)]) //eslint-disable-line

  return isMobile ? (
    <MobileNavbar title={title} />
  ) : (
    <Header>
      <div id="header-title" />
      <div className="inline-flex items-center gap-5">
        {extraContent}
        <div className="text-grey">
          <BellOutlined onClick={toggleVisibleNotificationModal} />
        </div>
        <Dropdown
          menu={[
            {
              key: 'ChangePassword',
              label: t('common:change password'),
              onClick: handleChangePassword,
              style: { textAlign: 'center' },
            },
            {
              key: 'SignOut',
              label: t('common:logout'),
              onClick: handleLogout,
              style: { textAlign: 'center' },
            },
          ]}>
          <Button type="link" className="ant-dropdown-link p-0">
            <LoginUser>
              <span>{pathOr('', ['displayName'], user)}</span>
              <CaretDownOutlined className="icon-caret-down" />
            </LoginUser>
          </Button>
        </Dropdown>
      </div>
      {openChangePasswordModal && (
        <ChangePasswordModal
          open={openChangePasswordModal}
          onCancel={() => setOpenChangePasswordModal(false)}
          onOk={() => setOpenChangePasswordModal(false)}
          title="修改密碼"
        />
      )}
      {visibleNotificationModal && (
        <NotificationLogModal
          open={visibleNotificationModal}
          onCancel={toggleVisibleNotificationModal}
        />
      )}
    </Header>
  )
}

const Header = styled(Layout.Header)`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  &.ant-layout-header {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 24px;
    padding-right: 16px;
    background: transparent;
    line-height: initial;
  }
`

const LoginUser = styled.div`
  display: flex;
  align-items: center;
  color: ${p => p.theme.font.secondary};
  font-size: 14px;
  font-weight: normal;

  .icon-caret-down {
    position: relative;
    top: 2px;
    margin-left: 4px !important;
    font-size: 14px !important;
  }
`

export default Navbar

const NotificationLogModal = (props: ModalProps) => {
  const { dataSource } = useChargeEventNotification({
    pageNum: 0,
    pageSize: 50,
  })
  const columns = [
    {
      title: '時間',
      dataIndex: 'createAtStr',
      key: 'createAtStr',
    },
    {
      title: '事件',
      dataIndex: 'eventStr',
      key: 'eventStr',
    },
    {
      title: '內容',
      dataIndex: 'content',
      key: 'content',
    },
  ]

  return (
    <Modal {...props} title="通知紀錄" width={800} footer={null}>
      <Table columns={columns} pagination={false} dataSource={dataSource} />
    </Modal>
  )
}
